import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  useTheme,
  useMediaQuery,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import { calculateUnitCost } from "../../../utils/costUtils";
import CostProfileRowDetails from "./CostProfileRowDetails";

const capWords = (s = "") =>
  s
    .toString()
    .trim()
    .split(/[\s_-]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
const norm = (s = "") => s.toString().trim().toLowerCase();

const extractSuffix = (displayName = "", base = "") => {
  const dn = (displayName || "").trim();
  const parts = dn
    .split(/—|-/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2) return "";
  if (norm(parts[0]) === norm(base)) return parts.slice(1).join(" - ");
  if (norm(parts[parts.length - 1]) !== norm(base))
    return parts[parts.length - 1];
  return "";
};

const inferFromMachineName = (machine = "", base = "") => {
  if (!machine) return "";
  const tokens = machine.split(/[\s_-]+/).filter(Boolean);
  const filtered = tokens.filter((t) => norm(t) !== norm(base));
  return filtered.length ? capWords(filtered[0]) : "";
};

function friendlyProductLabel(profile) {
  // Prefer productId doc if present, else snapshot
  const prod =
    profile && typeof profile.productId === "object"
      ? profile.productId
      : profile?.productSnapshot || null;

  if (!prod) return profile?.name || "—";

  // Base label: prefer displayName/name over subcategory
  const base =
    prod.displayName ||
    prod.name ||
    capWords(prod.subcategory || "") ||
    "Product";

  // 1) Prefer explicit flavor if present
  const flavor = capWords(prod.flavor || "");
  if (flavor) return `${base} — ${flavor}`;

  // 2) Parse suffix from displayName
  const parsedFromDisplay = extractSuffix(prod.displayName || "", base);
  if (parsedFromDisplay) return `${base} — ${parsedFromDisplay}`;

  // 3) Infer from machine name
  const inferred = inferFromMachineName(prod.name || "", base);
  if (inferred) return `${base} — ${inferred}`;

  return base;
}

/**
 * scope:
 *  - "products": only product profiles (default when used in Products tab)
 *  - "components": only reusable components
 *  - "all": show both
 */
const CostProfileTable = ({
  profiles,
  onEdit,
  onDelete,
  onSync,
  scope = "products",
}) => {
  const [openRowId, setOpenRowId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { products, components } = useMemo(() => {
    const comps = [];
    const prods = [];
    (profiles || []).forEach((p) =>
      p.isComponent ? comps.push(p) : prods.push(p)
    );
    return { products: prods, components: comps };
  }, [profiles]);

  const rows =
    scope === "components"
      ? components
      : scope === "all"
      ? [...components, ...products]
      : products;

  const renderRow = (p) => {
    const unitCost = calculateUnitCost(p);
    const isOpen = openRowId === p._id;
    const margin =
      !p.isComponent && p.unitPrice
        ? ((Number(p.unitPrice) - unitCost) / Number(p.unitPrice)) * 100
        : null;

    return (
      <React.Fragment key={p._id}>
        <TableRow
          hover
          onClick={() => setOpenRowId(isOpen ? null : p._id)}
          sx={{
            cursor: "pointer",
            backgroundColor: isOpen ? "#fafafa" : "inherit",
          }}>
          <TableCell sx={{ width: "36%" }}>
            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={friendlyProductLabel(p)}>
                {friendlyProductLabel(p)}
              </Typography>
            </Stack>
          </TableCell>
          <TableCell sx={{ width: "14%" }}>{p.type}</TableCell>
          <TableCell sx={{ width: "10%" }}>{p.batchSize || "—"}</TableCell>
          <TableCell sx={{ width: "12%" }}>
            {p.isComponent ? "—" : `$${Number(p.unitPrice || 0).toFixed(2)}`}
          </TableCell>
          <TableCell sx={{ width: "12%" }}>${unitCost.toFixed(2)}</TableCell>
          <TableCell sx={{ width: "12%" }}>
            {margin !== null ? `${margin.toFixed(1)}%` : "—"}
          </TableCell>
          <TableCell sx={{ width: "8%" }}>
            <Tooltip title="Edit">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(p);
                }}
                size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sync">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onSync(p);
                }}
                size="small">
                <SyncIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(p);
                }}
                size="small"
                color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>

        <CostProfileRowDetails profile={p} open={isOpen} />
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ overflowX: isMobile ? "auto" : "visible", width: "100%" }}>
      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell>Product</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Unit Price</TableCell>
            <TableCell>Unit Cost</TableCell>
            <TableCell>Margin</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map(renderRow)
          ) : (
            <TableRow>
              <TableCell
                colSpan={7}
                align="center"
                sx={{ py: 3, color: "text.secondary" }}>
                No cost profiles found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default CostProfileTable;
